import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Combobox, Transition } from '@headlessui/react';
import cn from 'classnames';

import { HiOutlineChevronDoubleDown } from 'react-icons/hi';

function Select({
  data,
  name,
  value,
  readOnly,
  onChange,
  inputClassName,
  containerClassName,
  optionsItemClassName,
  optionsContainerClassName,
}) {
  const [selected, setSelected] = useState(value);
  const [query, setQuery] = useState('');

  const filteredData =
    query === ''
      ? data
      : data.filter((item) =>
          item.value
            .toLowerCase()
            .includes(query.toLowerCase().replace(/\s+/g, '')),
        );

  const handleClickClear = () => setSelected(data?.[0]);

  useEffect(() => {
    onChange((prev) => ({
      ...prev,
      [name]: selected,
    }));
  }, [selected, name, onChange]);

  return (
    <Combobox value={selected} onChange={setSelected}>
      <div className={cn('w-[260px] relative', containerClassName)}>
        <div className="relative w-full text-left bg-white rounded-sm cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-teal-300 focus-visible:ring-offset-2 sm:text-sm overflow-hidden">
          <Combobox.Input
            className={cn(
              'w-full bg-light-gray-50 border-none text-gray-500 focus:ring-0 py-[0.6rem] pl-3 pr-10 font-[500] leading-5 text-xs',
              inputClassName,
            )}
            displayValue={(item) => item.label}
            onChange={(e) => setQuery(e.target.value)}
            readOnly={readOnly}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <HiOutlineChevronDoubleDown
              className="w-3 h-3 text-gray-500"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options
            className={cn(
              'absolute z-20 w-full top-10 left-0 shadow-lg',
              optionsContainerClassName,
            )}
          >
            {filteredData.length === 0 && query !== '' ? (
              <div className="cursor-default bg-white select-none relative py-2 px-4 text-gray-700 text-center">
                Nothing found.
              </div>
            ) : (
              filteredData.map((item) => (
                <Combobox.Option
                  key={item.value}
                  value={item}
                  className={({ active }) =>
                    `cursor-default text-xs select-none relative py-2 pl-3 pr-4 bg-white text-gray-500 font-[500] ${optionsItemClassName} ${
                      active ? 'bg-gray-100' : 'text-gray-500'
                    }`
                  }
                >
                  {item.label}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>

        {selected.value !== data?.[0].value && (
          <div className="absolute -right-20 top-1 hidden">
            <button
              type="button"
              className="text-red-500 text-xs font-semibold"
              onClick={handleClickClear}
            >
              CLEAR ALL
            </button>
          </div>
        )}
      </div>
    </Combobox>
  );
}

Select.defaultProps = {
  readOnly: true,
  inputClassName: '',
  containerClassName: '',
  optionsItemClassName: '',
  optionsContainerClassName: '',
};

Select.propTypes = {
  readOnly: PropTypes.bool,
  inputClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  optionsItemClassName: PropTypes.string,
  optionsContainerClassName: PropTypes.string,

  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  data: PropTypes.instanceOf(Array).isRequired,
  value: PropTypes.instanceOf(Object).isRequired,
};

export default Select;
